import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "@helpers/axios/private.axios";
import { useTranslation } from "@helpers/translate";
import { ALERT } from "../../../../../../../constants";

const RISK_SETTINGS_URL = "/futures/risk-settings";
const SAVE_DELAY_MS = 500;

/**
 * @typedef {{ mode: '$' | '%', takeProfit: number, stopLoss: number }} RiskSettings
 */

/**
 * Loads the user's saved TP/SL settings and saves them with a delay,
 * so typing doesn't send a request per key. A guest has nothing saved.
 *
 * @param {(settings: RiskSettings) => void} onLoad stable callback
 * @returns {(settings: RiskSettings|null) => void} null removes the saved settings
 */
export const useSavedRiskSettings = (onLoad) => {
  const dispatch = useDispatch();
  const { __ } = useTranslation();
  const userId = useSelector((state) => state.user.user?.id);
  const userIdRef = useRef(userId);
  const timerRef = useRef(null);
  const pendingRef = useRef(null);
  const sendRef = useRef(null);

  userIdRef.current = userId;
  sendRef.current = async ({ settings }) => {
    try {
      if (settings) {
        await axios.put(RISK_SETTINGS_URL, {
          mode: settings.mode,
          take_profit: settings.takeProfit,
          stop_loss: settings.stopLoss,
        });
      } else {
        await axios.delete(RISK_SETTINGS_URL);
      }
    } catch (error) {
      dispatch({ type: ALERT.ALERT_ERROR, payload: __("common.error") });
    }
  };

  useEffect(() => {
    if (!userId) return undefined;

    let isActual = true;

    axios
      .get(RISK_SETTINGS_URL)
      .then(({ data }) => {
        if (!isActual || !data.data) return;

        onLoad({
          mode: data.data.mode,
          takeProfit: data.data.take_profit,
          stopLoss: data.data.stop_loss,
        });
      })
      .catch(() => {
        // Nothing to apply, the form stays empty
      });

    return () => {
      isActual = false;
    };
  }, [userId, onLoad]);

  // A change waiting for the delay still gets saved when the form goes away
  useEffect(
    () => () => {
      clearTimeout(timerRef.current);

      if (pendingRef.current) sendRef.current(pendingRef.current);
    },
    [],
  );

  return useCallback((settings) => {
    if (!userIdRef.current) return;

    clearTimeout(timerRef.current);
    pendingRef.current = { settings };
    timerRef.current = setTimeout(() => {
      const pending = pendingRef.current;

      pendingRef.current = null;
      sendRef.current(pending);
    }, SAVE_DELAY_MS);
  }, []);
};
